import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MouseFooter from "../components/MouseFooter";
import "../styles/claim.css";

const API = process.env.REACT_APP_CLAIM_API || "http://localhost:3032";

export default function Claim() {
  const [tab, setTab]               = useState("claim");
  const [menuOpen, setMenuOpen]     = useState(false);

  // Presale claim state
  const [file, setFile]             = useState(null);
  const [password, setPassword]     = useState("");
  const [dragging, setDragging]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [result, setResult]         = useState(null);
  const fileInputRef                = useRef();

  // Grant activation state
  const [grantFile, setGrantFile]         = useState(null);
  const [grantDragging, setGrantDragging] = useState(false);
  const [grantLoading, setGrantLoading]   = useState(false);
  const [grantError, setGrantError]       = useState("");
  const [grantResult, setGrantResult]     = useState(null);
  const [parsedVoucher, setParsedVoucher] = useState(null);
  const grantFileInputRef                 = useRef();

  const navigate = useNavigate();

  const goToSection = (e, id) => {
    e.preventDefault();
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
    setMenuOpen(false);
  };

  const switchTab = (t) => {
    setTab(t);
    setError("");
    setGrantError("");
  };

  // ── Presale claim ────────────────────────────────────────────────────────────
  const handleFile = (f) => {
    if (f && f.name.endsWith(".json")) {
      setFile(f);
      setError("");
    } else {
      setError("Please upload a valid IOU .json file.");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !password) return;
    setLoading(true);
    setError("");

    const fd = new FormData();
    fd.append("iou_file", file);
    fd.append("password", password);

    try {
      const resp = await fetch(`${API}/claim`, { method: "POST", body: fd });
      const data = await resp.json();
      if (!resp.ok || data.error) {
        setError(data.error || "Something went wrong. Please try again.");
      } else {
        setResult(data);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Grant activation ─────────────────────────────────────────────────────────
  const parseGrantFile = (f) => {
    if (!f) return;
    setGrantFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const v = JSON.parse(e.target.result);
        if (!v.ego_grant || v.version !== 1) {
          setGrantError("This does not look like an Ego grant file.");
          setParsedVoucher(null);
          return;
        }
        setGrantError("");
        setParsedVoucher(v);
      } catch {
        setGrantError("Could not read file — make sure it is a valid .json grant file.");
      }
    };
    reader.readAsText(f);
  };

  const handleGrantDrop = (e) => {
    e.preventDefault();
    setGrantDragging(false);
    if (e.dataTransfer.files[0]) parseGrantFile(e.dataTransfer.files[0]);
  };

  const handleGrantSubmit = async (e) => {
    e.preventDefault();
    if (!grantFile) return;
    setGrantLoading(true);
    setGrantError("");

    const fd = new FormData();
    fd.append("grant_file", grantFileInputRef.current.files[0]);

    try {
      const resp = await fetch(`${API}/activate-grant`, { method: "POST", body: fd });
      const data = await resp.json();
      if (!resp.ok || data.error) {
        setGrantError(data.error || "Something went wrong. Try again.");
      } else {
        setGrantResult(data);
      }
    } catch {
      setGrantError("Network error — please try again.");
    } finally {
      setGrantLoading(false);
    }
  };

  return (
    <div className="app">
      <header className="navbar">
        <div className="logo">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="/ego.png" alt="Ego Blockchain Logo" className="logo-img" />
          </Link>
        </div>
        <button className={`hamburger ${menuOpen ? "active" : ""}`} onClick={() => setMenuOpen(!menuOpen)}>
          <span /><span /><span />
        </button>
        <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
          <Link to="/download" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Download Ego Desktop</Link>
          <Link to="/blog"    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Blog</Link>
          <Link to="/docs"    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Docs</Link>
          <Link to="/egoscan" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>EgoScan</Link>
          <Link to="/claim"   onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Claim EGOC</Link>
        </nav>
      </header>

      {menuOpen && <div className="menu-overlay" onClick={() => setMenuOpen(false)} />}

      <main className="claim-page">
        <div className="claim-card">

          {/* Tabs */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "28px" }}>
            <button
              onClick={() => switchTab("claim")}
              style={{
                flex: 1, padding: "10px", borderRadius: "10px", border: "none",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
                background: tab === "claim" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "#1a1a2e",
                color: tab === "claim" ? "#fff" : "#64748b",
                transition: "all 0.2s",
              }}
            >
              Claim EGOC
            </button>
            <button
              onClick={() => switchTab("grant")}
              style={{
                flex: 1, padding: "10px", borderRadius: "10px", border: "none",
                fontSize: "13px", fontWeight: 700, cursor: "pointer",
                background: tab === "grant" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "#1a1a2e",
                color: tab === "grant" ? "#fff" : "#64748b",
                transition: "all 0.2s",
              }}
            >
              Activate Grant
            </button>
          </div>

          {/* ── Presale claim tab ── */}
          {tab === "claim" && (
            !result ? (
              <>
                <div className="claim-header">
                  <div className="claim-badge">SEED ROUND</div>
                  <h1>Claim Your EGOC</h1>
                  <p className="claim-subtitle">
                    Submit your presale IOU file to lock in your Genesis Block allocation.
                    Your EGOC will be minted to your mainnet address at chain launch.
                  </p>
                </div>

                <div className="claim-info-box">
                  You need your <strong>IOU file</strong> (<code>ego-presale-iou--.json</code>) and
                  the <strong>password</strong> you chose at purchase. Both are required — without them
                  your claim cannot be verified.
                </div>

                <form className="claim-form" onSubmit={handleSubmit}>
                  <div className="claim-field">
                    <label>IOU File</label>
                    <div
                      className={`claim-drop ${dragging ? "drag-over" : ""} ${file ? "has-file" : ""}`}
                      onClick={() => fileInputRef.current.click()}
                      onDragOver={e => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={handleDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        style={{ display: "none" }}
                        onChange={e => handleFile(e.target.files[0])}
                      />
                      {file ? (
                        <>
                          <div className="claim-drop-icon">📄</div>
                          <div className="claim-drop-filename">{file.name}</div>
                          <div className="claim-drop-hint">Click to change file</div>
                        </>
                      ) : (
                        <>
                          <div className="claim-drop-icon">⬆</div>
                          <div className="claim-drop-text">Drop your IOU file here or <span>browse</span></div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="claim-field">
                    <label>IOU Password</label>
                    <input
                      type="password"
                      className="claim-input"
                      placeholder="Password you set at purchase…"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                    />
                  </div>

                  {error && <div className="claim-error">{error}</div>}

                  <button type="submit" className="claim-btn" disabled={!file || !password || loading}>
                    {loading ? "Verifying…" : "Submit Claim →"}
                  </button>
                </form>
              </>
            ) : (
              <div className="claim-success">
                <div className="claim-success-icon">✅</div>
                <h2>Claim Recorded</h2>
                <div className="claim-success-amount">
                  {result.egoc_amount?.toLocaleString("en-US", { maximumFractionDigits: 4 })} EGOC
                </div>
                <div className="claim-success-label">credited at Genesis Block</div>
                {result.mainnet_address && (
                  <div className="claim-success-addr-wrap">
                    <div className="claim-success-label" style={{ marginBottom: 6 }}>Mainnet address</div>
                    <div className="claim-success-addr">{result.mainnet_address}</div>
                  </div>
                )}
                <div className="claim-success-note">
                  Your allocation is confirmed. <strong>No further action needed.</strong> When Ego Chain
                  launches, your EGOC will be minted to the address above automatically.
                </div>
                <Link to="/" className="claim-home-btn">Back to Home</Link>
              </div>
            )
          )}

          {/* ── Grant activation tab ── */}
          {tab === "grant" && (
            !grantResult ? (
              <>
                <div className="claim-header">
                  <div className="claim-badge">EGOC GRANT</div>
                  <h1>Activate Your Grant</h1>
                  <p className="claim-subtitle">
                    Upload the grant file you received from the Ego team to lock in your allocation.
                    Your mainnet address will be collected before Genesis Block launch.
                  </p>
                </div>

                {parsedVoucher && (
                  <div style={{
                    background: "linear-gradient(135deg,#1a1040,#0f1530)",
                    border: "1px solid #6366f133", borderRadius: "14px",
                    padding: "20px", marginBottom: "20px",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                      <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#a78bfa" }}>
                        {parsedVoucher.grant_type?.charAt(0).toUpperCase() + parsedVoucher.grant_type?.slice(1)} Grant
                      </span>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>{parsedVoucher.label || ""}</span>
                    </div>
                    <div style={{
                      fontSize: "34px", fontWeight: 900,
                      background: "linear-gradient(90deg,#a78bfa,#67e8f9)",
                      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                    }}>
                      {(parsedVoucher.allocation?.egoc_amount || 0).toLocaleString("en-US")} EGOC
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "14px" }}>credited at Genesis Block</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {[
                        { val: parsedVoucher.allocation?.vest_months ?? "—", lbl: "Vest months" },
                        { val: parsedVoucher.allocation?.cliff_months ?? "—", lbl: "Cliff months" },
                      ].map(({ val, lbl }) => (
                        <div key={lbl} style={{
                          flex: 1, background: "#0f0f1a", border: "1px solid #2d2d4e",
                          borderRadius: "10px", padding: "10px 12px", textAlign: "center",
                        }}>
                          <div style={{ fontSize: "18px", fontWeight: 800, color: "#e2e8f0" }}>{val}</div>
                          <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>{lbl}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <form className="claim-form" onSubmit={handleGrantSubmit}>
                  <div className="claim-field">
                    <label>Grant File</label>
                    <div
                      className={`claim-drop ${grantDragging ? "drag-over" : ""} ${grantFile ? "has-file" : ""}`}
                      onClick={() => grantFileInputRef.current.click()}
                      onDragOver={e => { e.preventDefault(); setGrantDragging(true); }}
                      onDragLeave={() => setGrantDragging(false)}
                      onDrop={handleGrantDrop}
                    >
                      <input
                        ref={grantFileInputRef}
                        type="file"
                        accept=".json"
                        style={{ display: "none" }}
                        onChange={e => parseGrantFile(e.target.files[0])}
                      />
                      {grantFile ? (
                        <>
                          <div className="claim-drop-icon">📄</div>
                          <div className="claim-drop-filename">{grantFile.name}</div>
                          <div className="claim-drop-hint">Click to change file</div>
                        </>
                      ) : (
                        <>
                          <div className="claim-drop-icon">⬆</div>
                          <div className="claim-drop-text">Drop your grant file here or <span>browse</span></div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="claim-info-box">
                    No wallet needed right now — just activate your grant to lock in your allocation.
                    We'll contact you before Genesis Block launch to collect your mainnet address.
                  </div>

                  {grantError && <div className="claim-error">{grantError}</div>}

                  <button type="submit" className="claim-btn" disabled={!grantFile || !parsedVoucher || grantLoading}>
                    {grantLoading ? "Activating…" : parsedVoucher ? "Activate Grant →" : "Upload grant file first"}
                  </button>
                </form>
              </>
            ) : (
              <div className="claim-success">
                <div className="claim-success-icon">✅</div>
                <h2>Grant Activated</h2>
                <div className="claim-success-amount">
                  {(grantResult.egoc_amount || 0).toLocaleString("en-US")} EGOC
                </div>
                <div className="claim-success-label">locked in — credited at Genesis Block</div>
                <div style={{
                  background: "#1a1a2e", border: "1px solid #2d2d4e",
                  borderRadius: "10px", padding: "14px 16px",
                  fontSize: "12px", color: "#94a3b8", lineHeight: 1.7, width: "100%", textAlign: "left",
                }}>
                  <strong style={{ color: "#c084fc" }}>Vesting:</strong>{" "}
                  {grantResult.vest_months || 0}-month vest with a {grantResult.cliff_months || 0}-month cliff.<br /><br />
                  <strong style={{ color: "#c084fc" }}>Claim ID:</strong><br />
                  <span style={{ fontFamily: "monospace", fontSize: "11px", color: "#e2e8f0", wordBreak: "break-all" }}>
                    {grantResult.id}
                  </span>
                </div>
                <div className="claim-success-note">
                  Your allocation is confirmed and locked in.{" "}
                  <strong style={{ color: "#facc15" }}>Save your Claim ID above.</strong>{" "}
                  Before Genesis Block launch, the Ego team will contact you to collect your mainnet address.
                </div>
                <Link to="/" className="claim-home-btn">Back to Home</Link>
              </div>
            )
          )}

        </div>
      </main>

      <MouseFooter handleScroll={goToSection} />
    </div>
  );
}
